import React, {
  Suspense,
  Fragment,
  lazy
} from 'react';
import {
  Switch,
  Redirect,
  Route
} from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';

type Routes = {
  exact?: boolean;
  path?: string | string[];
  guard?: any;
  layout?: any;
  component?: any;
  routes?: Routes;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes
                    ? renderRoutes(route.routes)
                    : <Component {...props} />}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes: Routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    exact: true,
    path: '/access-denied',
    component: lazy(() => import('src/views/errors/AccessDenied'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },
  {
    exact: true,
    path: '/register-unprotected',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app/management/emissions',
        component: lazy(() => import('src/views/emissions/EmissionsView'))
      },
      {
        exact: true,
        path: '/app/management/emission',
        component: lazy(() => import('src/views/emissions/EmissionDetail'))
      },
      {
        exact: true,
        path: '/app/management/evicertia_signing',
        component: lazy(() => import('src/views/evicertia_signing/SigningView'))
      },
      {
        exact: true,
        path: '/app/management/client-data',
        component: lazy(() => import('src/views/client-data/ClientDataView'))
      },
      {
        exact: true,
        path: '/app/management/plans-settings',
        component: lazy(() => import('src/views/plans-settings/PlanSettingsView'))
      },
      {
        exact: true,
        path: '/app/reports/dashboard',
        component: lazy(() => import('src/views/reports/DashboardView'))
      },
      {
        exact: true,
        path: '/app/monitoring/health',
        component: lazy(() => import('src/views/monitoring/ServicesHealthView'))
      },
      {
        exact: true,
        path: '/app/management/brokers',
        component: lazy(() => import('src/views/brokers'))
      },
      {
        exact: true,
        path: '/app/management/reporterunifiedusers',
        component: lazy(() => import('src/views/reporterUnifiedUsers'))
      },
      {
        exact: true,
        path: '/app/management/users',
        component: lazy(() => import('src/views/user'))
      },
      {
        exact: true,
        path: '/app/management/broker-b2c-users',
        component: lazy(() => import('src/views/broker-b2c-users'))
      },
      {
        exact: true,
        path: '/app/security/menus',
        component: lazy(() => import('src/views/security'))
      },
      {
        exact: true,
        path: '/app/settings/payment-conditions',
        component: lazy(() => import('src/views/settings'))
      },
      {
        exact: true,
        path: '/app/settings/banks',
        component: lazy(() => import('src/views/settings/banks'))
      },
      {
        exact: true,
        path: '/app/management/sign-pending',
        component: lazy(() => import('src/views/emissions/SignPending'))
      },
      {
        exact: true,
        path: '/app/management/lock-persons',
        component: lazy(() => import('src/views/emissions/LockPersons'))
      },
      {
        exact: true,
        path: '/app/management/citizen-search',
        component: lazy(() => import('src/views/emissions/CitizenSearch'))
      },
      {
        exact: true,
        path: '/app/management/get-signed-documents',
        component: lazy(() => import('src/views/emissions/GetSignedDocuments'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '*',
    routes: [
      {
        exact: true,
        guard: GuestGuard,
        path: '/',
        component: lazy(() => import('src/views/auth/LoginView'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

export default routes;
